import { Role, rolesManipulator } from 'auth';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import VaultsListItem from '@/modules/common/components/items/VaultsListItem.tsx';
import CreateVaultModal from '@/modules/common/components/modals/CreateVaultModal.tsx';
import EditVaultModal from '@/modules/common/components/modals/EditVaultModal.tsx';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import {
  useCreateVaultMutation,
  useDeleteVaultMutation,
  useUpdateVaultMutation,
  useVaultsQuery,
} from '@/modules/common/queries/secret-manager.ts';
import {
  Button,
  ConfirmModal,
  DropdownMenuItem,
  EmptyListState,
  ModalProviderContext,
  PageLayout,
  SearchField,
  Spinner,
} from 'ui';
import { VaultResponse } from 'compliance-api';

const SecretManager = () => {
  const role = rolesManipulator.current_user_role as Role | undefined;
  const isAdmin = role === Role.COMPLIANCE_ADMIN;
  const { openModal } = useContext(ModalProviderContext);
  const navigate = useNavigate();
  const businessId = useGetCurrentBusinessId();

  const [vaultSearch, setVaultSearch] = useState('');

  useEffect(() => {
    setVaultSearch('');
  }, [businessId]);

  const { data: vaultsData, isLoading: isVaultsLoading } = useVaultsQuery({
    page: 1,
    page_size: 100,
    search: vaultSearch || undefined,
  });

  const createVaultMutation = useCreateVaultMutation();
  const updateVaultMutation = useUpdateVaultMutation();
  const deleteVaultMutation = useDeleteVaultMutation();

  const basePath = isAdmin ? '/admin/secret-manager' : '/employee/secret-manager';

  const openVault = (vault: VaultResponse) => {
    navigate(`${basePath}/${vault.id}`);
  };

  const handleOpenCreateVaultModal = () => {
    openModal(CreateVaultModal, {
      onCreate: async payload => {
        const response = await createVaultMutation.mutateAsync(payload);
        navigate(`${basePath}/${response.vault.id}`);
      },
    });
  };

  const getVaultMenuOptions = (vault: VaultResponse): DropdownMenuItem[] => [
    {
      label: 'Edit',
      onClick: () =>
        openModal(EditVaultModal, {
          vault,
          onSave: async payload => {
            await updateVaultMutation.mutateAsync(payload);
          },
        }),
    },
    {
      label: 'Delete',
      onClick: () =>
        openModal(ConfirmModal, {
          onConfirm: async () => {
            await deleteVaultMutation.mutateAsync({ vaultId: vault.id });
          },
          title: (
            <p>
              Are you sure you want to delete <span className="text-blue-700">{vault.name}</span>?
            </p>
          ),
        }),
    },
  ];

  const headerAction = (
    <div className="w-full min-w-0 mt-0 flex flex-col sm:flex-row justify-end sm:items-center gap-3">
      <div className="w-full min-w-0 sm:max-w-[420px]">
        <SearchField
          placeholder="Search vaults"
          value={vaultSearch}
          onChange={setVaultSearch}
          className="w-full min-w-0"
        />
      </div>
      {isAdmin && (
        <Button
          variant="primary"
          className="h-11 px-5 whitespace-nowrap w-full min-w-0 sm:w-fit shrink-0"
          onClick={handleOpenCreateVaultModal}
        >
          Create vault
        </Button>
      )}
    </div>
  );

  return (
    <PageLayout
      title={<span className="whitespace-nowrap">Vaults</span>}
      headerAction={headerAction}
      classNameTitle="flex-col items-stretch gap-3 sm:flex-row sm:justify-between sm:items-center"
    >
      {isVaultsLoading ? (
        <div className="w-full py-12 flex justify-center">
          <Spinner size="md" />
        </div>
      ) : (
        <>
          <EmptyListState
            title={vaultSearch ? 'No vaults found' : 'No vaults yet'}
            description={
              vaultSearch
                ? 'Try changing your search query.'
                : 'To create a vault, click Create vault. Vaults help organise and control passwords/secrets access. Vaults without people added are accessible only to you. If you want to share it with other people in your organisation, you could do it in the vault settings.'
            }
            itemsCount={(vaultsData?.data || []).length}
            isLoading={false}
            isSearchUsed={!!vaultSearch}
          />

          {!!vaultsData?.data?.length && (
            <div className="w-full flex flex-col space-y-2 mt-3">
              {(vaultsData.data || []).map(vault => (
                <VaultsListItem
                  key={vault.id}
                  vault={vault}
                  isAdmin={isAdmin}
                  onOpenVault={openVault}
                  menuOptions={getVaultMenuOptions(vault)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </PageLayout>
  );
};

export default SecretManager;
